import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Observable } from 'rxjs';
import { FormBase } from '../../../_classes/form-base.class';
import { IClinicalHistoryStructureSummary } from '../../../_interfaces/IClinicalHistoryStructure';
import ISite from '../../../_interfaces/ISite';
import { EResources } from '../../../_resources/EResources';
import { ClinicalHistoryStructuresService } from '../../../_services/clinical-history-structures/clinical-history-structures.service';
import { CustomersService } from '../../../_services/customers/customers.service';
import { SessionService } from '../../../_services/session/session.service';
import { SitesService } from '../../../_services/sites/sites.service';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { AdminFormActionsComponent } from '../../admin-ui/admin-form-actions/admin-form-actions.component';

@Component({
  selector: 'app-form-site',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    CommonModule,
    InputTextModule,
    DropdownModule,
    AdminFormActionsComponent,
  ],
  templateUrl: './form-site.component.html',
  styleUrl: './form-site.component.scss',
})
export class FormSiteComponent extends FormBase<ISite> implements OnInit {
  @Input() showIntro = true;
  @Input() showClinicalHistoryStructureControl = true;
  public readonly colorPresets = [
    '#2563eb',
    '#0ea5e9',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#ec4899',
    '#334155',
  ];
  public submitted = false;
  public submitting = false;
  public costHint: { currency_code: string; effective_price: number; additional_charge: boolean } | null = null;
  public loadingCostHint = false;
  public clinicalHistoryStructures: IClinicalHistoryStructureSummary[] = [];
  public loadingClinicalHistoryStructures = false;

  public form: ReturnType<FormBuilder['group']>;

  constructor(
    public readonly session: SessionService,
    private readonly siteService: SitesService,
    private readonly validationService: ValidatorService,
    public readonly messageService: MessageService,
    public readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly customersService: CustomersService,
    private readonly clinicalHistoryStructuresService: ClinicalHistoryStructuresService,
  ) {
    super(session, messageService, translate);
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      address: [''],
      city: [''],
      province: [''],
      country_code: ['ES'],
      postal_code: [''],
      email: ['', [Validators.email]],
      color: ['#3b82f6'],
      phone: [''],
      clinical_history_structure_id: [null],
    });
  }

  async execute(): Promise<void> {
    this.submitted = true;
    this.clearErrors();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    this.payload = {
      name: raw.name || '',
      address: raw.address || '',
      city: raw.city || '',
      province: raw.province || '',
      country_code: this.normalizeCountryCode(raw.country_code),
      postal_code: raw.postal_code || '',
      email: raw.email || '',
      color: raw.color || '#3b82f6',
      phone: raw.phone || '',
    };

    if (this.showClinicalHistoryStructureControl) {
      this.payload.clinical_history_structure_id =
        raw.clinical_history_structure_id || null;
    }

    this.submitting = true;
    try {
      await super.execute();
    } finally {
      this.submitting = false;
    }
  }

  clear(): void {
    this.payload = {
      name: '',
      address: '',
      city: '',
      province: '',
      country_code: 'ES',
      postal_code: '',
      email: '',
      phone: '',
      color: '#3b82f6',
      clinical_history_structure_id: null,
    };

    this.form.reset({
      name: '',
      address: '',
      city: '',
      province: '',
      country_code: 'ES',
      postal_code: '',
      email: '',
      phone: '',
      color: '#3b82f6',
      clinical_history_structure_id: null,
    });

    this.submitted = false;
  }

  generatePayload(): ISite {
    return this.payload;
  }

  getResourceName(): string {
    return EResources.SITES;
  }

  updateFn(payload: ISite): Observable<any> {
    return this.siteService.update(this.customers_id, {
      ...this.sanitizeSitePayload(payload),
      id: this.id,
    });
  }

  createFn(payload: ISite): Observable<any> {
    return this.siteService.create(
      this.customers_id,
      this.sanitizeSitePayload(payload),
    );
  }

  properties(): string[] {
    const fields = [
      'name',
      'address',
      'city',
      'province',
      'country_code',
      'postal_code',
      'email',
      'phone',
      'color',
    ];

    if (this.showClinicalHistoryStructureControl) {
      fields.push('clinical_history_structure_id');
    }

    return fields;
  }

  get clinicalHistoryStructureOptions() {
    return [
      {
        label: this.translate.instant('ADMIN.SITES.DETAIL.CLINICAL_HISTORY_STRUCTURE_NONE'),
        value: null,
      },
      ...this.clinicalHistoryStructures.map((structure) => ({
        label: structure.name,
        value: structure.id,
      })),
    ];
  }

  validate(): Promise<void> {
    return this.validationService.site(this.payload);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadCostHint();
    this.loadClinicalHistoryStructures();
    this.form.patchValue({
      name: this.payload?.name || '',
      address: this.payload?.address || '',
      city: this.payload?.city || '',
      province: this.payload?.province || '',
      country_code: this.normalizeCountryCode(this.payload?.country_code),
      postal_code: this.payload?.postal_code || '',
      email: this.payload?.email || '',
      phone: this.payload?.phone || '',
      color: this.normalizeColor(this.payload?.color || '#3b82f6'),
      clinical_history_structure_id:
        this.payload?.clinical_history_structure_id || null,
    });

  }

  private loadClinicalHistoryStructures(): void {
    if (!this.customers_id) {
      this.clinicalHistoryStructures = [];
      return;
    }
    this.loadingClinicalHistoryStructures = true;
    this.clinicalHistoryStructuresService.list(this.customers_id).subscribe({
      next: (structures) => {
        this.clinicalHistoryStructures = structures || [];
        this.loadingClinicalHistoryStructures = false;
      },
      error: () => {
        this.clinicalHistoryStructures = [];
        this.loadingClinicalHistoryStructures = false;
      },
    });
  }

  private loadCostHint(): void {
    if (this.mode !== 'create' || !this.customers_id) {
      this.costHint = null;
      return;
    }
    this.loadingCostHint = true;
    this.customersService.getCustomerCostHints(this.customers_id).subscribe({
      next: (hints) => {
        this.costHint = {
          currency_code: `${hints?.currency_code || 'EUR'}`,
          effective_price: Number(hints?.site?.effective_price || 0),
          additional_charge: Number(hints?.site?.effective_price || 0) > 0,
        };
        this.loadingCostHint = false;
      },
      error: () => {
        this.costHint = null;
        this.loadingCostHint = false;
      },
    });
  }

  onColorInput(rawValue: unknown): void {
    this.form.controls['color'].setValue(this.normalizeColor(rawValue));
  }

  get colorPreview(): string {
    return this.normalizeColor(this.form.controls['color'].value || '#3b82f6');
  }

  get formTitleKey(): string {
    return this.mode === 'edit' ? 'ADMIN.SITES.DETAIL.GENERAL_TITLE' : 'ADMIN.SITES.NEW';
  }

  get formDescriptionKey(): string {
    return this.mode === 'edit'
      ? 'ADMIN.SITES.DETAIL.GENERAL_DESCRIPTION'
      : 'ADMIN.IA.SITES.DESCRIPTION';
  }

  selectColorPreset(color: string): void {
    this.onColorInput(color);
  }

  private normalizeColor(value: unknown): string {
    if (value && typeof value === 'object') {
      const color = value as { r?: number; g?: number; b?: number };
      if (
        Number.isFinite(color.r) &&
        Number.isFinite(color.g) &&
        Number.isFinite(color.b)
      ) {
        const toHex = (channel: number) =>
          Math.max(0, Math.min(255, Math.round(channel)))
            .toString(16)
            .padStart(2, '0');
        return `#${toHex(color.r as number)}${toHex(color.g as number)}${toHex(color.b as number)}`;
      }
    }

    const normalized = `${value || ''}`.trim().replace('#', '');
    if (!normalized) {
      return '#3b82f6';
    }

    if (/^[0-9a-fA-F]{3}$/.test(normalized) || /^[0-9a-fA-F]{6}$/.test(normalized)) {
      return `#${normalized}`;
    }

    return '#3b82f6';
  }

  private sanitizeSitePayload(payload: ISite): ISite {
    const normalizedPayload: ISite = {
      name: payload?.name || '',
      address: payload?.address || '',
      city: payload?.city || '',
      province: payload?.province || '',
      country_code: this.normalizeCountryCode(payload?.country_code),
      postal_code: payload?.postal_code || '',
      email: payload?.email || '',
      phone: payload?.phone || '',
      color: this.normalizeColor(payload?.color || '#3b82f6'),
    } as ISite;

    if (this.showClinicalHistoryStructureControl) {
      normalizedPayload.clinical_history_structure_id =
        payload?.clinical_history_structure_id || null;
    }

    return normalizedPayload;
  }

  private normalizeCountryCode(rawValue: unknown): string {
    const raw = `${rawValue || ''}`.trim();
    if (!raw) return 'ES';

    const normalized = raw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();

    const aliases: Record<string, string> = {
      ES: 'ES',
      ESP: 'ES',
      ESPANA: 'ES',
      ESPAÑA: 'ES',
      SPAIN: 'ES',
    };

    if (aliases[normalized]) {
      return aliases[normalized];
    }

    if (/^[A-Z]{2,5}$/.test(normalized)) {
      return normalized;
    }

    return 'ES';
  }
}
